/**
 * The map view, laid out for the player (D209): which of the pack's offered
 * styles they are looking at, and the drawing in that style.
 *
 * @remarks
 * The host describes the map — the graph per flavor, the offered styles,
 * the opening one — and the shell chooses and draws. This module is the
 * choosing and the drawing as one pure function, beside the projection
 * that produced the view, so the two halves of the contract sit in one
 * package and a shell needs no opinion of its own about how a preference
 * meets a pack.
 *
 * The preference is the player's across games, so a chosen style the
 * current pack does not OFFER is not an error: it reads as the pack's
 * opening style, and the next game may honor it. A pack never shows a
 * style it did not list (Mike, 2026-09-25), so the choice is always an
 * element of `styles`.
 *
 * D210: the scene needs a renderer the browser may not have. Where the
 * shell says it cannot draw one, a chosen scene is drawn as the hologram —
 * the same levelled graph, so nothing is missing from the wire — and the
 * result says so, so the panel can tell the player in one line.
 *
 * @packageDocumentation
 */

import type { MapGraph, MapView } from '@orpheus/api-contracts';
import type { MapStyle } from '@orpheus/content-schema';

import { layOutMapGraph, type MapDrawing, mapGraphFlavorOf, type MapLayout } from '@orpheus/engine-runtime';

/**
 * The player's word on how the map is drawn. `'default'` defers to the
 * pack's opening style; a style names the one they chose. The same
 * three-way shape as the clock's preference (D153), for the same reason: a
 * player who never touched the chooser gets each pack's own answer.
 */
export type MapStylePreference = MapStyle | 'default';

/** The map as drawn: the style it is drawn in, the layout, and the drawn graph's own annotations. */
export interface LaidOutMapView {
	/** Links the drawn graph could not place honestly — on the plan, every `up` and `down`; on the hologram, only the outward and the odd. */
	readonly annotations: MapGraph['annotations'];
	/** D210: true when the player chose the scene and the shell could not draw it, so the hologram stands in. */
	readonly isSceneStandIn: boolean;
	readonly layout: MapLayout;
	/**
	 * The style the player is looking at — the preferred one where the pack
	 * offers it, else the pack's opening style; always one of `styles`, so
	 * the chooser can mark it. What is DRAWN is `layout.style`: the same
	 * word, except when a scene the browser cannot draw is drawn as the
	 * hologram (D215: the one way a hologram is ever drawn), and then
	 * `isSceneStandIn` says so.
	 */
	readonly style: MapStyle;
}

/** What the shell knows that the host does not. */
export interface LayOutMapViewOptions {
	/**
	 * D224: whether the figure is drawn at a phone's width — below the shell's
	 * rail breakpoint. The hologram, standing in for the scene, then follows
	 * the scene's answer to crowding: stubs unnamed, walked names a size down.
	 * Absent, the figure is wide.
	 */
	readonly isNarrow?: boolean;
	/** Whether this browser can draw a scene at all (WebGL); absent, assumed so. */
	readonly isSceneDrawable?: boolean;
}

/**
 * Which offered style a preference selects.
 *
 * @param map - The map view
 * @param preference - The player's preference
 * @returns The preferred style when the pack offers it; else the pack's opening style
 */
export const chooseMapStyle = (map: MapView, preference: MapStylePreference): MapStyle =>
	preference !== 'default' && map.styles.includes(preference) ? preference : map.style;

/**
 * Lay the map out in the style the preference selects.
 *
 * @param map - The map view
 * @param preference - The player's preference
 * @param options - {@link LayOutMapViewOptions}
 * @returns The style drawn, its layout, and the drawn graph's annotations
 * @throws When the view carries no graph in the flavor the chosen style needs — a host that offered a style it did not describe
 */
export const layOutMapView = (map: MapView, preference: MapStylePreference, options: LayOutMapViewOptions = {}): LaidOutMapView => {
	const style = chooseMapStyle(map, preference);
	const isSceneStandIn = style === 'scene' && options.isSceneDrawable === false;
	const drawing: MapDrawing = isSceneStandIn ? 'holo' : style;
	const flavor = mapGraphFlavorOf(drawing);
	const graph = map.graphs[flavor];

	if (graph === undefined) {
		throw new Error(`the map view offers the "${style}" style but carries no ${flavor} graph to draw it from`);
	}

	return {
		annotations: graph.annotations,
		isSceneStandIn,
		layout: layOutMapGraph(graph, drawing, map.tint, options.isNarrow === undefined ? {} : { isNarrow: options.isNarrow }),
		style,
	};
};
